// Écran d'étiquetage des marques.
//
// La marque n'est PAS dans les factures : `facture_ligne.laboratoire` porte le
// distributeur (« Alliance Healthcare »), et « Avène » n'apparaît que 9 fois
// dans 40 682 libellés. Elle ne peut donc venir que du titulaire. Cet écran est
// la seule pièce de l'app dont le carburant est SON temps, pas de la machine.
//
// Tout est donc dessiné pour qu'une minute rapporte le maximum :
//   · les groupes arrivent du plus lourd au plus léger — 88 groupes couvrent
//     50 % des 2,77 M€, 441 en couvrent 80 % ;
//   · une décision = deux champs et un bouton, pas un formulaire ;
//   · l'autocomplétion propose ce qu'il a DÉJÀ écrit, parce que sa taxonomie
//     contient des raccourcis non conventionnels et que c'est ça qui a de la
//     valeur — lui imposer une liste fermée la détruirait ;
//   · il peut s'arrêter n'importe quand : la progression est affichée en
//     permanence, en montant d'abord.

import { ordonnerGroupes, progression, suggereNonProduit } from "../lib/etiquetage-logic.js";
import { eur } from "../lib/format.js";

const el = (tag, className, texte) => {
  const n = document.createElement(tag);
  if (className) n.className = className;
  if (texte != null) n.textContent = texte;
  return n;
};

/** Toutes les marques et tous les groupes déjà saisis, pour l'autocomplétion. */
function alimenterSuggestions(racine, decisions) {
  const marques = new Set(), groupes = new Set();
  for (const d of decisions || []) {
    if (d?.marque) marques.add(d.marque);
    if (d?.groupe) groupes.add(d.groupe);
  }
  for (const [id, valeurs] of [["liste-marques", marques], ["liste-groupes", groupes]]) {
    const dl = racine.querySelector(`#${id}`);
    if (!dl) continue;
    dl.replaceChildren(...[...valeurs].sort().map((v) => {
      const o = document.createElement("option");
      o.value = v;
      return o;
    }));
  }
}

function rendreProgression(p) {
  const bloc = el("div", "etq-progression");
  bloc.appendChild(el("div", "etq-resume", p.resume));

  const jauge = el("div", "etq-jauge");
  const barre = el("div", "etq-jauge-in");
  // La jauge suit le MONTANT, pas le nombre de groupes : c'est la seule des deux
  // mesures qui dise si le référentiel commence à servir à quelque chose.
  barre.style.width = `${Math.min(100, p.pct_ht)}%`;
  jauge.appendChild(barre);
  bloc.appendChild(jauge);

  bloc.appendChild(el("div", "etq-detail",
    `${eur(p.ht_couvert)} attribués sur ${eur(p.ht_total)} · ` +
    `${p.groupes_faits} groupe${p.groupes_faits > 1 ? "s" : ""} décidé${p.groupes_faits > 1 ? "s" : ""}`));
  return bloc;
}

/**
 * Le dépliant « code par code » d'un groupe.
 *
 * POURQUOI IL EXISTE
 * ------------------
 * Un groupe n'a pas toujours UNE réponse. La clé « CREME » rassemble 86 lignes et
 * 8 marques distinctes — Avril, Bioderma, CeraVe, Horace, La Rosée… Lui demander
 * « quelle marque ? » est une question sans réponse possible, et y répondre quand
 * même salirait le référentiel sur 86 lignes d'un coup.
 *
 * CE QU'IL NE FAIT PAS, ET C'EST VOLONTAIRE
 * ------------------------------------------
 * Décider ici n'inscrit RIEN dans etiquette_groupe : seuls les codes cochés sont
 * tranchés. Marquer le groupe ferait disparaître de la file le reliquat non
 * traité sans que rien ne le signale. La file travaillant au niveau du code, le
 * groupe s'allège tout seul et sort quand il ne reste plus rien.
 *
 * La sélection multiple plutôt qu'une saisie par ligne : dans « CREME », on coche
 * les trois lignes Bioderma, on tape la marque UNE fois, on valide. Puis les deux
 * CeraVe. C'est le geste réel, et il évite 86 frappes.
 */
function rendreDepliant(g, surDecision, chargerCodes) {
  const zone = el("div", "etq-detail");

  const ouvrir = el("button", "etq-detail-btn",
    `Détailler les ${g.codes} code${g.codes > 1 ? "s" : ""}`);
  ouvrir.type = "button";
  zone.appendChild(ouvrir);

  ouvrir.addEventListener("click", async () => {
    ouvrir.disabled = true;
    ouvrir.textContent = "Chargement…";
    let codes;
    try {
      codes = (await chargerCodes(g.cle))?.codes || [];
    } catch (e) {
      // On rend la main : un dépliant qui reste muet laisse croire que le groupe
      // n'a pas de détail, alors que c'est la requête qui a échoué.
      ouvrir.disabled = false;
      ouvrir.textContent = `Échec du chargement — réessayer`;
      return;
    }
    ouvrir.remove();
    if (!codes.length) {
      zone.appendChild(el("div", "muted", "Tous les codes de ce groupe sont déjà traités."));
      return;
    }

    const liste = el("div", "etq-codes");
    const cases = [];
    for (const c of codes) {
      const ligne = el("label", "etq-code");
      const cb = el("input", null);
      cb.type = "checkbox";
      cb.value = c.code;
      cases.push(cb);
      ligne.appendChild(cb);
      ligne.appendChild(el("span", "etq-code-lib", c.libelle || c.code));
      ligne.appendChild(el("span", "etq-code-ht", eur(c.ht)));
      liste.appendChild(ligne);
    }
    zone.appendChild(liste);

    const barre = el("div", "etq-codes-actions");
    const tout = el("button", "etq-lien", "Tout cocher");
    tout.type = "button";
    const mq = el("input", "etq-champ");
    mq.placeholder = "Marque de la sélection";
    mq.setAttribute("list", "liste-marques");
    mq.autocapitalize = "characters";
    const gr = el("input", "etq-champ");
    gr.placeholder = "Groupe";
    gr.setAttribute("list", "liste-groupes");
    gr.autocapitalize = "characters";
    const ok = el("button", "etq-ok", "Appliquer");
    ok.type = "button";
    const non = el("button", "etq-non", "Pas un produit");
    non.type = "button";
    const compte = el("span", "etq-compte", "0 sélectionné");

    barre.appendChild(tout);
    barre.appendChild(mq);
    barre.appendChild(gr);
    barre.appendChild(ok);
    barre.appendChild(non);
    barre.appendChild(compte);
    zone.appendChild(barre);

    const selection = () => cases.filter((c) => c.checked && !c.disabled).map((c) => c.value);
    const maj = () => {
      const n = selection().length;
      compte.textContent = `${n} sélectionné${n > 1 ? "s" : ""}`;
      ok.disabled = non.disabled = (n === 0);
    };
    maj();
    liste.addEventListener("change", maj);
    tout.addEventListener("click", () => {
      const cible = cases.some((c) => !c.checked && !c.disabled);
      for (const c of cases) if (!c.disabled) c.checked = cible;
      maj();
    });

    const envoyer = async (nonProduit) => {
      const sel = selection();
      if (!sel.length) return;
      if (!nonProduit && !mq.value.trim()) { mq.focus(); return; }
      ok.disabled = non.disabled = true;
      compte.textContent = "Enregistrement…";
      try {
        await surDecision({
          groupe_cle: g.cle,
          marque: nonProduit ? null : mq.value.trim(),
          groupe: nonProduit ? null : (gr.value.trim() || null),
          non_produit: nonProduit,
          codes: sel,
        });
      } catch (e) {
        ok.disabled = non.disabled = false;
        compte.textContent = "Échec — rien n'est enregistré.";
        return;
      }
      // Les lignes traitées sortent de la liste au lieu de rester cochées : ce
      // qui est fait ne doit plus pouvoir être re-sélectionné par mégarde.
      for (const c of cases) {
        if (sel.includes(c.value)) {
          c.disabled = true;
          c.checked = false;
          c.closest(".etq-code")?.classList.add("fait");
        }
      }
      mq.value = "";
      gr.value = "";
      maj();
      if (cases.every((c) => c.disabled)) {
        zone.appendChild(el("div", "etq-etat", "Groupe entièrement traité."));
      }
    };
    ok.addEventListener("click", () => envoyer(false));
    non.addEventListener("click", () => envoyer(true));
  });

  return zone;
}

function rendreCarte(g, surDecision, chargerCodes) {
  const carte = el("div", "etq-carte");

  const tete = el("div", "etq-tete");
  tete.appendChild(el("div", "etq-cle", g.cle));
  tete.appendChild(el("div", "etq-poids",
    `${eur(g.ht)} · ${g.lignes} ligne${g.lignes > 1 ? "s" : ""} · ${g.codes} code${g.codes > 1 ? "s" : ""}`));
  carte.appendChild(tete);

  // Les libellés réels sont ce qui permet de reconnaître « AV » comme Avène.
  // Sans eux, une clé abrégée est indéchiffrable.
  const ex = el("ul", "etq-exemples");
  for (const e of (g.exemples || []).slice(0, 3)) ex.appendChild(el("li", null, e));
  carte.appendChild(ex);

  const form = el("form", "etq-form");
  const champMarque = el("input", "etq-champ");
  champMarque.placeholder = "Marque — ex. AVENE";
  champMarque.setAttribute("list", "liste-marques");
  champMarque.autocapitalize = "characters";
  // Le catalogue para propose parfois une valeur. Ce n'est PAS une réponse : il
  // est exact mais son NIVEAU est inconstant (parfois la marque, parfois le
  // groupe). On pré-remplit pour épargner une frappe, il corrige si besoin.
  if (g.suggestion) champMarque.value = g.suggestion;

  const champGroupe = el("input", "etq-champ");
  champGroupe.placeholder = "Groupe — ex. PIERRE FABRE";
  champGroupe.setAttribute("list", "liste-groupes");
  champGroupe.autocapitalize = "characters";

  form.appendChild(champMarque);
  form.appendChild(champGroupe);

  const valider = el("button", "etq-ok", "Valider");
  valider.type = "submit";
  const rejeter = el("button", "etq-non", "Pas un produit");
  rejeter.type = "button";
  // Suggestion seulement : on met en évidence sans cocher. Un faux positif
  // pré-coché ne serait pas relu — c'est le plus coûteux des deux sens.
  if ((g.exemples || []).some(suggereNonProduit)) rejeter.classList.add("suggere");

  // Les deux boutons dans leur propre rangée : laissés au fil du `flex-wrap`,
  // « Valider » se collait au champ Groupe et « Pas un produit » tombait seul sur
  // une troisième ligne. Sur un écran qu'on utilise en rafale, les deux gestes
  // possibles doivent être côte à côte et toujours au même endroit.
  const actions = el("div", "etq-actions");
  actions.appendChild(valider);
  actions.appendChild(rejeter);
  form.appendChild(actions);
  carte.appendChild(form);

  // Le dépliant n'apparaît que s'il y a plusieurs codes : sur un groupe à code
  // unique, la décision de groupe et la décision de code sont la même chose, et
  // proposer les deux ferait hésiter sans rien apporter.
  if (chargerCodes && g.codes > 1) carte.appendChild(rendreDepliant(g, surDecision, chargerCodes));

  const dire = (msg) => {
    valider.disabled = rejeter.disabled = true;
    carte.classList.add("etq-envoi");
    carte.appendChild(el("div", "etq-etat", msg));
  };

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const marque = champMarque.value.trim();
    if (!marque) { champMarque.focus(); return; }
    dire("Enregistrement…");
    surDecision({ groupe_cle: g.cle, marque, groupe: champGroupe.value.trim() || null, non_produit: false }, carte);
  });
  rejeter.addEventListener("click", () => {
    dire("Enregistrement…");
    surDecision({ groupe_cle: g.cle, marque: null, groupe: null, non_produit: true }, carte);
  });

  return carte;
}

/**
 * Rend l'écran complet.
 * `donnees` = { groupes, couverture, decisions } — `decisions` alimente
 * l'autocomplétion et peut manquer sans casser l'écran.
 */
export function rendreEtiquetage(racine, donnees, surDecision, chargerCodes) {
  const frag = document.createDocumentFragment();

  frag.appendChild(rendreProgression(progression(donnees?.couverture)));

  // Les deux listes d'autocomplétion sont créées une fois et remplies après.
  for (const id of ["liste-marques", "liste-groupes"]) {
    const dl = document.createElement("datalist");
    dl.id = id;
    frag.appendChild(dl);
  }

  const restants = ordonnerGroupes(donnees?.groupes || [], donnees?.decisions || []);
  if (!restants.length) {
    frag.appendChild(el("div", "muted", "Plus rien à étiqueter sur cette page."));
  }
  for (const g of restants) frag.appendChild(rendreCarte(g, surDecision, chargerCodes));

  racine.replaceChildren(frag);
  alimenterSuggestions(racine, donnees?.decisions);
}

/**
 * Charge une page de groupes à décider.
 * Une seule requête : la couverture voyage avec les groupes, parce qu'elle doit
 * se rafraîchir à chaque décision et qu'un second aller-retour la ferait
 * clignoter en retard.
 */
export async function chargerEtiquetage(api, limite = 25) {
  return await api("etiquetage_groupes", { limite });
}

/** Les codes non encore décidés d'un groupe, pour le dépliant. */
export async function chargerCodesGroupe(api, groupeCle) {
  return await api("etiquetage_codes", { groupe_cle: groupeCle });
}
